import React, { useState, useRef, useEffect } from "react";
import { api, API } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Send, Loader2, ShieldAlert } from "lucide-react";

export const Intelligence = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const containerRef = useRef(null);

  const briefQ = useQuery({
    queryKey: ["ai-brief"],
    queryFn: async () => (await api.post("/ai/brief")).data,
  });

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "ai", text: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("Stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trimStart();
          if (payload === "[DONE]") continue;
          if (payload.startsWith("[ERROR]")) { acc += `\n\n_${payload}_`; continue; }
          acc += payload;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "ai", text: acc };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "ai", text: "Intelligence engine unreachable. Try again shortly." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="page-intelligence">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Cadastre Intelligence Engine</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Sparkles className="text-[#C99A2E]" /> Sovereign Intelligence
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">
          GPT-powered executive analysis. Recommendations are advisory — human statutory approval remains mandatory.
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <aside className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Executive brief</div>
          {briefQ.isLoading && <div className="mt-3 text-[13px] text-[#B3C2CC] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Composing brief…</div>}
          {briefQ.data && (
            <>
              <div className="text-[14px] text-[#F6F8FA] mt-3 leading-relaxed" data-testid="ai-brief-text">{briefQ.data.brief}</div>
              {(briefQ.data.conflicts?.length > 0 || briefQ.data.expiring?.length > 0) && (
                <div className="mt-4 space-y-2 text-[12px]">
                  {briefQ.data.conflicts?.slice(0, 3).map((c, i) => (
                    <div key={i} className="p-2 border border-[#A93F3F]/40 rounded-sm bg-[#A93F3F]/10 text-[#DCE3E8]">
                      <span className="font-mono-cad text-[#E67E7E]"><ShieldAlert size={11} className="inline"/> {c.application_number}</span> — {c.commodity} · {c.province}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 text-[10px] font-mono-cad uppercase tracking-[0.22em] text-[#7C93A5]">Generated {briefQ.data.generated_at?.slice(0, 19).replace("T"," ")}</div>
            </>
          )}
        </aside>

        <div className="lg:col-span-2 cad-panel flex flex-col overflow-hidden" style={{ minHeight: 520 }}>
          <div className="px-5 py-3 border-b cad-hairline flex items-center gap-2">
            <Sparkles size={14} className="text-[#C99A2E]" />
            <div className="font-display font-bold text-[#F6F8FA]">Ask the Intelligence Engine</div>
          </div>
          <div ref={containerRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="ai-chat">
            {messages.length === 0 && (
              <div className="text-[13px] text-[#B3C2CC]">
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5] mb-2">Try asking</div>
                <div className="grid gap-2">
                  {[
                    "Give me a briefing on lithium licence conflicts.",
                    "Which provinces have the highest untitled parcel counts?",
                    "Draft an executive memo on expiring gold licences.",
                    "Summarise this week's compliance signals.",
                  ].map((s) => (
                    <button key={s} onClick={() => setInput(s)} data-testid={`ai-suggest-${s.slice(0, 12)}`}
                            className="cad-btn-outline text-left px-3 py-2 rounded-sm text-[12px]">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-sm text-[13.5px] whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#145A7A] text-[#F6F8FA]"
                    : "bg-[#0B3558] border cad-hairline text-[#F6F8FA]"
                }`}>{m.text || (streaming ? "…" : "")}</div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t cad-hairline flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                   data-testid="ai-input"
                   placeholder="Ask about mineral rights, land backlog, revenue leakage…"
                   className="flex-1 bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2.5 text-[13.5px] text-[#F6F8FA] focus:ring-2 focus:ring-[#C99A2E] outline-none" />
            <button onClick={send} disabled={streaming || !input.trim()} data-testid="ai-send"
                    className="cad-btn-primary px-4 rounded-sm inline-flex items-center gap-2 text-sm">
              {streaming ? <Loader2 size={14} className="animate-spin"/> : <Send size={14} />} Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
